/**
 * Team Service - Simplified Multi-User System
 * 
 * STRUCTURE:
 * - report-user-ids/{adminId} - Admin user document
 * - report-user-ids/{adminId}/users/{userId} - Team members
 * - report-user-ids/{adminId}/projects/{projectId} - Projects
 * 
 * ROLES/TAGS: admin, developer, designer, compliance, tester (just for identification)
 * PERMISSIONS: Everyone can see and edit everything (no restrictions)
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// ==========================================
// TYPES
// ==========================================

export interface TeamMember {
  id: string;
  email: string;
  password: string;
  name: string;
  tag: 'admin' | 'developer' | 'designer' | 'compliance' | 'tester';
  status: 'active' | 'inactive';
  adminId: string; // Which admin they belong to
  createdAt: Date;
  lastLogin?: Date;
}

export interface LoginResult {
  userId: string;
  email: string;
  name: string;
  tag: string;
  adminId: string;
  isAdmin: boolean; // true if logged in as main admin, false if team member
  adminEmail?: string; // For team members, stores admin's email
  accessToken?: string;
  refreshToken?: string;
  orgId?: string;
  organizationName?: string;
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

const convertTimestamp = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (timestamp.toDate) return timestamp.toDate();
  return new Date(timestamp);
};

// ==========================================
// AUTHENTICATION
// ==========================================

// const AUTH_SERVER_URL = 'https://fairpatterns-auth-server-413055756357.europe-west1.run.app/login';
const AUTH_SERVER_URL = `${process.env.REACT_APP_URL}/auth/login`;
const TOOL_ID = process.env.REACT_APP_TOOL_ID;
/**
 * Login via auth server - Primary login path
 */
const loginViaAuthServer = async (
  email: string,
  password: string
): Promise<LoginResult | null> => {
  const response = await fetch(AUTH_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, tool_id: TOOL_ID }),
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();

  // Map auth server response to LoginResult
  return {
    userId: data.user_id ?? data.userId ?? data.id ?? data.uid ?? '',
    email: data.email ?? email,
    name: data.name ?? data.displayName ?? email.split('@')[0],
    tag: data.tag ?? data.role ?? 'developer',
    adminId: data.adminId ?? data.user_id ?? data.userId ?? data.id ?? '',
    isAdmin: data.isAdmin ?? data.role === 'admin' ?? false,
    adminEmail: data.adminEmail,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    orgId: data.org_id,
    organizationName: data.organization_name,
  };
};

/**
 * Login via Firestore - Fallback (existing logic)
 */
const loginViaFirestore = async (
  email: string,
  password: string
): Promise<LoginResult | null> => {
  try {
    // STEP 1: Check if it's an admin login (report-user-ids collection)
    const adminsRef = collection(db, 'report-user-ids');
    const adminQuery = query(adminsRef, where('email', '==', email));
    const adminSnapshot = await getDocs(adminQuery);

    for (const adminDoc of adminSnapshot.docs) {
      const adminData = adminDoc.data();
      if (adminData.password === password) {
        // Admin login successful
        return {
          userId: adminDoc.id,
          email: adminData.email,
          name: adminData.name || adminData.email.split('@')[0],
          tag: 'admin',
          adminId: adminDoc.id,
          isAdmin: true,
        };
      }
    }

    // STEP 2: Check team members across all admins
    const allAdmins = await getDocs(collection(db, 'report-user-ids'));

    for (const adminDoc of allAdmins.docs) {
      const usersRef = collection(db, 'report-user-ids', adminDoc.id, 'users');
      const usersQuery = query(usersRef, where('email', '==', email));
      const usersSnapshot = await getDocs(usersQuery);

      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        if (userData.password === password && userData.status === 'active') {
          // Update last login
          await updateDoc(userDoc.ref, { lastLogin: Timestamp.now() });

          // Team member login successful
          const adminData = adminDoc.data();
          return {
            userId: userDoc.id,
            email: userData.email,
            name: userData.name,
            tag: userData.tag || 'developer',
            adminId: adminDoc.id,
            isAdmin: false,
            adminEmail: adminData.email,
          };
        }
      }
    }

    return null; // Login failed
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Login - Try auth server first, fallback to Firestore
 */
export const loginUser = async (
  email: string,
  password: string
): Promise<LoginResult | null> => {
  try {
    // Primary: Auth server
    // const authResult = await loginViaAuthServer(email, password);
    // if (authResult) return authResult;

    // Fallback: Firestore (existing logic)
    // return await loginViaFirestore(email, password);
    return await loginViaAuthServer(email, password);

  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// ==========================================
// TEAM MEMBER MANAGEMENT
// ==========================================

/**
 * Create team member
 */
export const createTeamMember = async (
  adminId: string,
  email: string,
  password: string,
  name: string,
  tag: 'admin' | 'developer' | 'designer' | 'compliance' | 'tester'
): Promise<TeamMember> => {
  try {
    // Check if email already exists
    const usersRef = collection(db, 'report-user-ids', adminId, 'users');
    const existingQuery = query(usersRef, where('email', '==', email));
    const existing = await getDocs(existingQuery);

    if (!existing.empty) {
      throw new Error('A user with this email already exists');
    }

    const now = Timestamp.now();
    const userData = {
      email: email.trim(),
      password,
      name: name.trim(),
      tag,
      status: 'active' as 'active' | 'inactive',
      createdAt: now,
    };

    const docRef = await addDoc(usersRef, userData);

    return {
      id: docRef.id,
      ...userData,
      adminId,
      createdAt: now.toDate(),
    };
  } catch (error) {
    console.error('Error creating team member:', error);
    throw error;
  }
};

/**
 * Get all team members for an admin
 */
export const getTeamMembers = async (adminId: string): Promise<TeamMember[]> => {
  try {
    const usersRef = collection(db, 'report-user-ids', adminId, 'users');
    const snapshot = await getDocs(usersRef);

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        email: data.email,
        password: data.password,
        name: data.name,
        tag: data.tag || 'developer',
        status: data.status || 'active',
        adminId,
        createdAt: convertTimestamp(data.createdAt),
        lastLogin: data.lastLogin ? convertTimestamp(data.lastLogin) : undefined,
      };
    });
  } catch (error) {
    console.error('Error fetching team members:', error);
    throw error;
  }
};

/**
 * Update team member
 */
export const updateTeamMember = async (
  adminId: string,
  userId: string,
  updates: Partial<TeamMember>
): Promise<void> => {
  try {
    const userRef = doc(db, 'report-user-ids', adminId, 'users', userId);
    const updateData: any = { ...updates };

    // Remove fields that shouldn't be updated
    delete updateData.id;
    delete updateData.adminId;
    delete updateData.createdAt;

    await updateDoc(userRef, updateData);
  } catch (error) {
    console.error('Error updating team member:', error);
    throw error;
  }
};

/**
 * Delete team member
 */
export const deleteTeamMember = async (adminId: string, userId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'report-user-ids', adminId, 'users', userId);
    await deleteDoc(userRef);
  } catch (error) {
    console.error('Error deleting team member:', error);
    throw error;
  }
};

/**
 * Toggle team member status
 */
export const toggleTeamMemberStatus = async (
  adminId: string,
  userId: string,
  currentStatus: 'active' | 'inactive'
): Promise<void> => {
  try {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    await updateTeamMember(adminId, userId, { status: newStatus });
  } catch (error) {
    console.error('Error toggling status:', error);
    throw error;
  }
};

/**
 * Get team statistics
 */
export const getTeamStats = async (adminId: string) => {
  try {
    const members = await getTeamMembers(adminId);

    return {
      total: members.length,
      active: members.filter(m => m.status === 'active').length,
      inactive: members.filter(m => m.status === 'inactive').length,
      byTag: {
        admin: members.filter(m => m.tag === 'admin').length,
        developer: members.filter(m => m.tag === 'developer').length,
        designer: members.filter(m => m.tag === 'designer').length,
        compliance: members.filter(m => m.tag === 'compliance').length,
        tester: members.filter(m => m.tag === 'tester').length,
      },
    };
  } catch (error) {
    console.error('Error fetching team stats:', error);
    throw error;
  }
};

// ==========================================
// PROJECT MANAGEMENT
// ==========================================

/**
 * Get projects path for an admin
 */
export const getProjectsCollection = (adminId: string) => {
  return collection(db, 'report-user-ids', adminId, 'projects');
};

/**
 * Get tasks path for a project
 */
export const getTasksCollection = (adminId: string, projectId: string) => {
  return collection(db, 'report-user-ids', adminId, 'projects', projectId, 'tasks');
};

/**
 * Get comments path for a task
 */
export const getCommentsCollection = (adminId: string, projectId: string, taskId: string) => {
  return collection(db, 'report-user-ids', adminId, 'projects', projectId, 'tasks', taskId, 'comments');
};

